import React from 'react';
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    Title,
    Tooltip,
    Legend,
} from 'chart.js';
import { BoxPlotController, BoxAndWiskers } from '@sgratzl/chartjs-chart-boxplot';
import { Chart } from 'react-chartjs-2';
import { getDisplayName, buildDisplayNameMap } from '../utils/portfolioNames';

ChartJS.register(
    CategoryScale,
    LinearScale,
    Title,
    Tooltip,
    Legend,
    BoxPlotController,
    BoxAndWiskers
);

const hexToRgba = (hex, alpha) => {
    const h = (hex || '').replace('#', '');
    if (h.length !== 6) return `rgba(0, 51, 102, ${alpha})`;
    const r = parseInt(h.substring(0, 2), 16);
    const g = parseInt(h.substring(2, 4), 16);
    const b = parseInt(h.substring(4, 6), 16);
    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

const BoxPlotChart = React.forwardRef(({ data, portfolioRegistry }, ref) => {
    if (!data) return null;

    const nameMap = buildDisplayNameMap(portfolioRegistry);
    const labels = data.map(d => nameMap[d.name] || getDisplayName(d.name));

    // Format data for box plot: [min, q1, median, q3, max]
    const boxPlotData = data.map(d => ({
        min: d.stats.min * 100,
        q1: d.stats.q1 * 100,
        median: d.stats.median * 100,
        q3: d.stats.q3 * 100,
        max: d.stats.max * 100
    }));

    const borderColors = data.map(d => d.colour || (d.is_blend ? '#f0a500' : '#072040'));
    const backgroundColors = borderColors.map(c => hexToRgba(c, 0.4));

    const chartData = {
        labels: labels,
        datasets: [
            {
                label: 'Annual Return Distribution',
                data: boxPlotData,
                backgroundColor: backgroundColors,
                borderColor: borderColors,
                borderWidth: 2,
                outlierColor: '#999',
                padding: 10,
                itemRadius: 0,
            },
        ],
    };

    const options = {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
            legend: { display: false },
            tooltip: {
                callbacks: {
                    label: function (context) {
                        const item = context.parsed;
                        return [
                            `Max: ${item.max.toFixed(1)}%`,
                            `Q3: ${item.q3.toFixed(1)}%`,
                            `Median: ${item.median.toFixed(1)}%`,
                            `Q1: ${item.q1.toFixed(1)}%`,
                            `Min: ${item.min.toFixed(1)}%`
                        ];
                    },
                },
            },
        },
        scales: {
            y: {
                title: { display: true, text: 'Annual Return (%)' },
                grid: { color: 'rgba(0, 0, 0, 0.05)' }
            },
            x: {
                grid: { display: false }
            }
        },
    };

    return <Chart ref={ref} type='boxplot' data={chartData} options={options} />;
});

export default BoxPlotChart;
